import React, { useState } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'react-toastify';
import RichTextEditor from './RichTextEditor';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { CURRENCY_SYMBOLS } from '../utils/currencySymbols';
import { FiLoader } from 'react-icons/fi';

const IncomeModal = ({ 
  onClose, 
  onSubmit, 
  categories = [], 
  updateCategories, 
  currencies = ['GBP'], 
  editingIncome = null, 
  selectedDate = new Date() 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: editingIncome?.title || '',
    amount: editingIncome?.amount ? Number(editingIncome.amount).toFixed(2) : '',
    category: editingIncome ? { 
      value: editingIncome.category, 
      label: editingIncome.category 
    } : null,
    description: editingIncome?.description || '',
    currency: editingIncome?.currency || currencies[0],
    date: editingIncome?.date ? format(editingIncome.date, 'yyyy-MM-dd') : format(selectedDate, 'yyyy-MM-dd'),
    id: editingIncome?.id || null
  });

  const categoryOptions = categories
    .map(cat => ({ value: cat, label: cat }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const currencyOptions = currencies.map(curr => ({ 
    value: curr, 
    label: `${curr} (${CURRENCY_SYMBOLS[curr]})`
  }));

  const handleCreateCategory = async (inputValue) => {
    const newCategory = inputValue.trim();
    if (newCategory && !categories.includes(newCategory)) {
      try {
        const newCategories = [...categories, newCategory];
        await updateCategories(newCategories);
        
        setFormData(prev => ({
          ...prev,
          category: { value: newCategory, label: newCategory }
        }));
        
        toast.success('Category created successfully');
      } catch (error) {
        console.error('Error creating category:', error);
        toast.error('Failed to create category');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    
    // Validate category is selected
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }

    setIsLoading(true);
    try {
      await onSubmit({
        ...formData,
        title: formData.title.trim(),
        category: formData.category.value, // Ensure we're sending the value, not the object
        currency: formData.currency,
        date: formData.date
      });
    } catch (error) {
      toast.error('Failed to save income');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAmountChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[^\d.]/g, '');
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }
    setFormData(prev => ({ ...prev, amount: value }));
  };

  const customStyles = {
    input: (base) => ({
      ...base,
      'input:focus': {
        boxShadow: 'none',
      },
    }),
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#6366f1' : '#e5e7eb',
      boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
      '&:hover': {
        borderColor: '#6366f1',
      },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#6366f1' 
        : state.isFocused 
          ? '#e0e7ff' 
          : 'white',
      color: state.isSelected ? 'white' : '#374151',
      '&:active': {
        backgroundColor: '#6366f1',
      },
    }),
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-xl h-2/3 2xl:h-max overflow-y-auto shadow-xl">
        <h3 className="text-xl font-semibold mb-6 text-gray-800 dark:text-gray-200">
          {editingIncome ? 'Edit Income' : 'Add New Income'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title
            </label>
            <input
              type="text"
              required
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 
                focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 
                transition-colors duration-200
                dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Enter income title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category
            </label>
            <CreatableSelect
              options={categoryOptions}
              value={formData.category}
              onChange={(selected) => setFormData(prev => ({ ...prev, category: selected }))}
              onCreateOption={handleCreateCategory}
              styles={customStyles}
              placeholder="Select or create a category"
              className="react-select-container"
              classNamePrefix="react-select"
              formatCreateLabel={(inputValue) => `Create category "${inputValue}"`}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date
            </label>
            <input
              type="date"
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 
                focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 
                transition-colors duration-200
                dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              required
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4 items-center">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Currency</label>
              <Select
                options={currencyOptions}
                value={currencyOptions.find(opt => opt.value === formData.currency)}
                onChange={(selected) => setFormData(prev => ({ 
                  ...prev, 
                  currency: selected.value 
                }))}
                styles={customStyles}
                isSearchable={false}
              />
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-500">
                  {CURRENCY_SYMBOLS[formData.currency]}
                </span>
                <input
                  type="number"
                  required
                  step="0.01"
                  className="w-full pl-8 pr-4 py-2.5 rounded-lg border border-gray-300 
                    focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 
                    transition-colors duration-200
                    dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                  value={formData.amount}
                  onChange={handleAmountChange}
                  onBlur={(e) => {
                    if (e.target.value && !isNaN(e.target.value)) {
                      setFormData(prev => ({
                        ...prev,
                        amount: Number(e.target.value).toFixed(2)
                      }));
                    }
                  }}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <RichTextEditor
              content={formData.description}
              onChange={(newContent) => setFormData(prev => ({ 
                ...prev, 
                description: newContent 
              }))}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 
                hover:bg-gray-50 dark:text-gray-300 dark:border-gray-600 
                dark:hover:bg-gray-700 transition-colors duration-200
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg 
                hover:bg-indigo-700 transition-colors duration-200
                disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <FiLoader className="animate-spin" />
                  {editingIncome ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                editingIncome ? 'Update Income' : 'Add Income'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

IncomeModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  categories: PropTypes.array,
  updateCategories: PropTypes.func,
  currencies: PropTypes.array,
  editingIncome: PropTypes.object,
  selectedDate: PropTypes.instanceOf(Date)
};

export default IncomeModal; 